import React, { useCallback, useMemo, useRef, useState } from "react";

import ShipeeziGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { Color } from "../../../../../../components/Form/Button";
import { DespatchShippingItem } from "../../../../../../models/despatch";
import { SearchAttribute } from "../../../../../../models/search.attribute";
import { Searches } from "../../../../../../models/shipeezi.table";
import { handleGetConvertValue } from "../../../../../../util/converts";
import { DecimalFormatType } from "../../../../../../util/formatDecimalValue";
import { getWeightMeasurement } from "../../../../../../util/getMeasurement";
import { paginate } from "../../../../../../util/paginate";
import { DespatchEmptySection } from "../../components/EmptySection";
import { PlusIcon } from "../../styles";
import useFilterShippingItems from "../hooks/useFilterShippingItems";
import ShippingItemsMaintenance, { ShippingItemsMaintenanceHandles } from "./Maintenance";
import { MainDiv, StyledButton } from "./styles";

const getIndex = (list?: Array<DespatchShippingItem>, row?: DespatchShippingItem): number => {
  return (
    list?.findIndex(
      (data) =>
        (data.id && row?.id && data.id === row.id) ||
        (data?.uuid && row?.uuid && data?.uuid === row?.uuid)
    ) ?? -1
  );
};

interface Props {
  shippingItems: DespatchShippingItem[];
  loading: boolean;
  viewOnly?: boolean;
  onUpdateItems: (data: DespatchShippingItem[]) => void;
}

const DespatchShippingItems = (props: Props) => {
  const { shippingItems, loading, onUpdateItems } = props;

  const { t: translate } = useTranslation();
  const maintenanceRef = useRef<ShippingItemsMaintenanceHandles>(null);

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const measurementWeight = getWeightMeasurement();
  const filteredData = useMemo(() => {
    let data = shippingItems;

    if (searchAttributes.length > 0) {
      data = useFilterShippingItems(shippingItems, searchAttributes);
    }

    return paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [shippingItems, searchAttributes, page, pageSize]);

  const renderAddShippingItem = useCallback(() => {
    return (
      <StyledButton customColor={Color.SECONDARY} onClick={() => maintenanceRef.current?.open()}>
        <PlusIcon color="#ffffff" size={18} />
        {translate("addShippingItems")}
      </StyledButton>
    );
  }, []);

  const onOpenEditShippingItem = (row: DespatchShippingItem) => {
    maintenanceRef.current?.open(row);
  };
  const onSubmitShippingItemMaintenance = (data: DespatchShippingItem) => {
    const success = data?.id || data?.uuid ? onEditShippingItem(data) : onCreateShippingItem(data);

    if (success) maintenanceRef.current?.close();
  };

  const onEditShippingItem = (row: DespatchShippingItem) => {
    const index = getIndex(shippingItems, row);

    if (index < 0) return;

    const cloned = _.cloneDeep(shippingItems);

    cloned.splice(index, 1, row);
    onUpdateItems(cloned);

    return true;
  };

  const onCreateShippingItem = (row: DespatchShippingItem) => {
    onUpdateItems([{ ...row, uuid: uuid() }, ...shippingItems]);

    return true;
  };

  const onRemoveShippingItem = (row: DespatchShippingItem) => {
    const index = getIndex(shippingItems, row);

    if (index < 0) return;

    const cloned = _.cloneDeep(shippingItems);

    cloned.splice(index, 1);
    onUpdateItems(cloned);
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("itemType"),
      hideSort: true,
    },
    {
      name: "description",
      title: translate("description"),
      hideSort: true,
    },
    {
      name: "length",
      title: translate("length"),
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "width",
      title: translate("width"),
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "height",
      title: translate("height"),
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "weight",
      title: `${translate("weight")} (${measurementWeight?.code})`,
      formatText: (weight: number) =>
        handleGetConvertValue(weight, measurementWeight?.code, DecimalFormatType.MAERSK),
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "volume",
      title: translate("quantity"),
      hideSort: true,
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch"],
      right: ["onAddShippingItem"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    onAddShippingItem: renderAddShippingItem(),
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const renderScreen = () => {
    if (!shippingItems || !shippingItems.length) {
      return (
        <DespatchEmptySection
          buttonLabel={translate("addShippingItems")}
          descriptionScreen={translate("shippingItems")}
          onAdd={() => maintenanceRef.current?.open()}
          maintenanceScreen
        />
      );
    }

    return (
      <MainDiv data-cy="despatch-shippingItems-container">
        <ShipeeziGrid
          data={filteredData}
          columns={columns}
          totalItems={shippingItems.length}
          loadingData={loading}
          emptyData={translate("noInformationToBeShown")}
          currentPage={page}
          onNextPage={setPage}
          onPreviousPage={setPage}
          onGoToPage={setPage}
          pageSize={pageSize}
          onChangePageSize={setPageSize}
          pageSizeList={[20, 40, 60, 100]}
          generalActions={generalActions}
          positionActionsColumn="right"
          rowActions={{
            handleEdit: (_, row) => onOpenEditShippingItem(row),
            handleDelete: (_, row) => onRemoveShippingItem(row),
          }}
        />
      </MainDiv>
    );
  };

  return (
    <React.Fragment>
      <ShippingItemsMaintenance ref={maintenanceRef} onSubmit={onSubmitShippingItemMaintenance} />

      {renderScreen()}
    </React.Fragment>
  );
};

export default DespatchShippingItems;
